/* eslint-disable react/no-array-index-key */
import React, { useEffect, useState, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Row, Col, UncontrolledTooltip } from 'reactstrap';
import { Controller, useForm } from 'react-hook-form';
import PropTypes, { shape } from 'prop-types';

import { selectCurrentClient } from 'redux/actions/clientActions';
import { getDocumentDetails } from 'redux/actions/documentActions';
import { goBackBasicData } from 'redux/actions/notificationActions';

import { ReactComponent as DocumentIcon } from 'assets/icon-document.svg';
import { ReactComponent as PdfIcon } from 'assets/icon-pdf.svg';
import { ReactComponent as WordIcon } from 'assets/icon-word.svg';
import { ReactComponent as ImgIcon } from 'assets/icon-img.svg';
import { ReactComponent as ExcelIcon } from 'assets/icon-excel.svg';
import { ReactComponent as InformationIcon } from 'assets/icon-information.svg';

import { LabelError, LabelRequiredInput } from 'shared/components/Labels';
import MainSpinner from 'shared/components/MainSpinner';
import { Button } from 'shared/components/Buttons';
import { TextField } from 'shared/components/form/Inputs';
import MultipleSelectSearch from 'shared/components/MultipleSelectSearch';
import DropdownSearch from 'shared/components/DropdownSearch';
import { colors } from 'shared/defaultTheme';
import AlertModal from 'shared/components/AlertModal';
import { cleanSpaceString } from 'shared/utils';

import { FormWrapper, GroupInputWrapper } from './styles';

const fileType = {
  pdf: 'application/pdf',
  pdfFile: '.pdf',
  wordFile: '.docx',
  excelFile: '.xlsx',
  jpgFile: '.jpeg',
  pngFile: '.png',
  svgFile: '.svg',
  excel: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  word: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  text: 'text/plain',
  jpg: 'image/jpeg',
  png: 'image/png',
  svg: 'image/svg+xml',
};

const ContactNotificationForm = ({
  onCancel,
  onSubmit,
  onToggleError,
  onOkSuccessRemove,
  showSuccessRemove,
  showError,
  newClient,
  setNewClient,
  clients,
  contacts,
  loadingCreate,
  loadingUpdate,
  selectedClient,
  errorMessage,
  disableButton,
  isDetailCommunication,
  isClientsSelectorDisabled,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { isCustomer, mail, admins, clientId } = useSelector((state) => state.auth);
  const notificationState = useSelector((state) => state.notification);
  const currentClient = useSelector((state) => state.clientState?.currentClient);
  const { newCommunication } = useSelector((state) => state.clientState);
  const newName = useRef(null);
  const [loadingContact, setLoadingContact] = useState(false);
  const [contactsDataSource, setContactsDataSource] = useState();
  const {
    control,
    handleSubmit,
    formState: { errors },
    watch,
    reset,
    setValue,
  } = useForm({
    mode: 'onBlur',
    reValidateMode: 'onChange',
    defaultValues: {
      ...notificationState?.currentNotification,
    },
  });
  const newCClient = newClient;
  newCClient.current = watch('clientId', '');
  newName.current = watch('name', '');

  useEffect(() => {
    if (!isCustomer) {
      setValue('clientId', clientId);
    }
  }, [isCustomer]);

  useEffect(() => {
    if (isDetailCommunication) {
      setValue('name', '');
      if (!isCustomer) {
        setValue('linkToContact', [mail]);
      }
    }
  }, [isDetailCommunication]);

  useEffect(() => {
    setTimeout(
      () => {
        if (newCommunication && newCommunication !== 'fromClient') {
          setValue('linkToContact', [newCommunication]);
          reset({
            linkToContact: [newCommunication],
          });
        }
        setValue('responsibles', [mail]);
        setLoadingContact(true);
      },
      newCommunication && newCommunication !== 'fromClient' ? 2500 : 800
    );
    return () => {
      setLoadingContact(false);
      dispatch(getDocumentDetails(null));
    };
  }, [newCommunication]);

  useEffect(() => {
    if (newCClient.current && !isDetailCommunication) {
      setNewClient(newClient.current);
      if (!isCustomer) {
        setValue('linkToContact', [mail]);
      }
      const findClient = clients.find((x) => x.clientId === newCClient.current);
      dispatch(selectCurrentClient(findClient));
    }
  }, [newCClient.current, clients, isDetailCommunication]);

  useEffect(() => {
    setContactsDataSource(
      contacts.map((contact) => {
        return contact.surname
          ? {
              ...contact,
              fullName: `${contact.name} ${contact.surname}`,
            }
          : { ...contact, fullName: contact.name };
      })
    );
  }, [contacts]);

  const IconFile = ({ file }) => {
    if (file.type === fileType.word || file.type === fileType.wordFile) {
      return <WordIcon fill={colors.suvaGrey} className="control-panel__icon-type" width="20" />;
    }
    if (file.type === fileType.pdf || file.type === fileType.pdfFile) {
      return <PdfIcon fill={colors.suvaGrey} className="control-panel__icon-type" width="20" />;
    }
    if (
      file.type === fileType.jpg ||
      file.type === fileType.png ||
      file.type === fileType.svg ||
      file.type === fileType.jpgFile ||
      file.type === fileType.pngFile ||
      file.type === fileType.svgFile
    ) {
      return <ImgIcon fill={colors.suvaGrey} className="control-panel__icon-type" width="20" />;
    }
    if (file.type === fileType.excel || file.type === fileType.excelFile) {
      return <ExcelIcon fill={colors.suvaGrey} className="control-panel__icon-type" width="20" />;
    }
    return <DocumentIcon fill={colors.suvaGrey} className="control-panel__icon-type" width="20" />;
  };

  IconFile.propTypes = {
    file: PropTypes.shape({
      type: PropTypes.string,
    }).isRequired,
  };

  return (
    <>
      <FormWrapper className="form" noValidate onSubmit={handleSubmit(onSubmit)}>
        <Row>
          <Col md="12" className="mb-4">
            <Row className="form-container">
              <Col md="12">
                <Row>
                  <Col>
                    <GroupInputWrapper className="group-input-container">
                      <div className="d-flex">
                        <LabelRequiredInput
                          label={t('notification_name_communication')}
                          color={colors.black}
                          className="label-input-notify"
                        />
                        <InformationIcon
                          id="notify-name-form-help"
                          height={17}
                          width={17}
                          className="ml-1 information-icon"
                          fill={colors.error}
                        />
                        <UncontrolledTooltip
                          placement="top"
                          target="notify-name-form-help"
                          autohide={false}
                        >
                          {t('notification_name_info')}
                        </UncontrolledTooltip>
                      </div>

                      <Controller
                        name="name"
                        control={control}
                        defaultValue=""
                        rules={{
                          required: { value: true, message: t('general_required_field') },
                        }}
                        render={({ field }) => (
                          <TextField
                            value={field.value}
                            onChange={(e) => {
                              const newEvent = cleanSpaceString(e);
                              field.onChange(newEvent);
                            }}
                            onBlur={field.onBlur}
                            error={errors.name !== undefined}
                            borderRadius={2}
                            inputClassName="w-100"
                            buttonClassName=""
                            withButtonClear={false}
                          />
                        )}
                      />
                      {errors.name && <LabelError label={errors.name.message} />}
                    </GroupInputWrapper>
                    <>
                      <GroupInputWrapper className="group-input-container">
                        <LabelRequiredInput
                          label={t('home_client_section_label')}
                          color={colors.black}
                          className="label-input-notify"
                        />
                        <Controller
                          name="clientId"
                          control={control}
                          defaultValue={currentClient?.clientId}
                          rules={{
                            required: { value: true, message: t('general_required_field') },
                          }}
                          render={({ field }) => (
                            <>
                              {!notificationState?.currentNotification && (
                                <DropdownSearch
                                  id="form-notification-dropdown-search"
                                  options={clients.map((item) => ({
                                    name: item.clientName,
                                    value: item.clientId,
                                  }))}
                                  value={field.value}
                                  onBlur={field.onBlur}
                                  onChange={field.onChange}
                                  error={errors.clientId !== undefined}
                                  isDisabled={!isCustomer || isClientsSelectorDisabled}
                                />
                              )}
                              {clients.length > 0 && notificationState?.currentNotification && (
                                <TextField
                                  value={selectedClient}
                                  onChange={() => null}
                                  borderRadius={2}
                                  inputClassName="w-100"
                                  buttonClassName=""
                                  withButtonClear={false}
                                  isReadOnly
                                />
                              )}
                            </>
                          )}
                        />
                        {errors.clientId && <LabelError label={errors.clientId.message} />}
                      </GroupInputWrapper>
                      <GroupInputWrapper className="group-input-container">
                        <LabelRequiredInput
                          label={t('client_management_contact')}
                          color={colors.black}
                          className="label-input-notify"
                        />
                        <Controller
                          name="linkToContact"
                          control={control}
                          defaultValue={[]}
                          rules={{
                            required: { value: true, message: t('general_required_field') },
                          }}
                          render={({ field }) => (
                            <>
                              {loadingContact && (
                                <MultipleSelectSearch
                                  dataSource={contactsDataSource}
                                  fields={{ text: 'fullName', value: 'mail' }}
                                  defaultValue={field.value.length > 0 ? field.value : undefined}
                                  onChange={field.onChange}
                                  onBlur={field.onBlur}
                                  error={errors.linkToContact !== undefined}
                                  titleNotFound={
                                    contactsDataSource.length > 0
                                      ? t('general_search')
                                      : t('contact_not_found')
                                  }
                                  isDisabled={!isCustomer}
                                />
                              )}
                            </>
                          )}
                        />
                        {errors.linkToContact && (
                          <LabelError label={errors.linkToContact.message} />
                        )}
                      </GroupInputWrapper>
                    </>
                    <GroupInputWrapper className="group-input-container">
                      <LabelRequiredInput
                        label={t('notification_person_responsible_field')}
                        color={colors.black}
                        className="label-input-notify"
                      />
                      <Controller
                        name="responsibles"
                        control={control}
                        defaultValue={[]}
                        rules={{
                          required: { value: true, message: t('general_required_field') },
                        }}
                        render={({ field }) => (
                          <>
                            {loadingContact && admins && admins.length > 0 && (
                              <MultipleSelectSearch
                                dataSource={admins.filter((x) => x.active)}
                                fields={{ text: 'nameComplete', value: 'mail' }}
                                defaultValue={field.value.length > 0 ? field.value : undefined}
                                onChange={field.onChange}
                                onBlur={field.onBlur}
                                error={errors.responsibles !== undefined}
                              />
                            )}
                          </>
                        )}
                      />
                      {errors.responsibles && <LabelError label={errors.responsibles.message} />}
                    </GroupInputWrapper>
                  </Col>
                </Row>
              </Col>
            </Row>
          </Col>
        </Row>
        <div className="ml-auto pt-2">
          {notificationState?.currentNotification ? (
            <>
              <Button
                className="home__btn text-uppercase"
                type="submit"
                label={t('general_button_create')}
                color="primary"
                disabled={disableButton}
              />
            </>
          ) : (
            <Button
              className="home__btn text-uppercase"
              type="submit"
              label={t('general_button_create')}
              color="primary"
              disabled={disableButton}
            />
          )}
          <Button
            className="home__btn text-uppercase mr-4"
            type="button"
            label={t('general_button_cancel')}
            color="secondary"
            onClick={() => {
              if (
                newCommunication &&
                newCommunication !== 'fromClient' &&
                newCommunication !== 'fromDocument'
              ) {
                onCancel();
                dispatch(goBackBasicData(true));
              } else {
                onCancel();
              }
            }}
          />
        </div>
      </FormWrapper>
      {(loadingCreate || loadingUpdate) && <MainSpinner />}
      <AlertModal
        type="error"
        isOpen={showError}
        toggle={onToggleError}
        message={errorMessage}
        okText={t('general_button_ok')}
        onOk={onToggleError}
      />
      <AlertModal
        type="success"
        isOpen={showSuccessRemove}
        toggle={onOkSuccessRemove}
        message={t('custom_feild_delete_document_success')}
        okText={t('general_button_ok')}
        onOk={onOkSuccessRemove}
      />
    </>
  );
};

ContactNotificationForm.defaultProps = {
  showSuccessRemove: undefined,
  file: undefined,
  setNewClient: null,
  selectedClient: '',
  errorMessage: '',
  isDetailCommunication: false,
  isClientsSelectorDisabled: false,
};
ContactNotificationForm.propTypes = {
  onCancel: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  onToggleError: PropTypes.func.isRequired,
  onOkSuccessRemove: PropTypes.func.isRequired,
  setNewClient: PropTypes.func,
  showSuccessRemove: PropTypes.bool,
  showError: PropTypes.bool.isRequired,
  newClient: PropTypes.shape({
    current: PropTypes.string,
  }).isRequired,
  clients: PropTypes.arrayOf(
    shape({
      id: PropTypes.string,
      clientName: PropTypes.string,
    })
  ).isRequired,
  contacts: PropTypes.arrayOf(shape({})).isRequired,
  file: PropTypes.shape({
    name: PropTypes.string,
    size: PropTypes.number,
  }),
  errorMessage: PropTypes.string,
  loadingCreate: PropTypes.bool.isRequired,
  loadingUpdate: PropTypes.bool.isRequired,
  selectedClient: PropTypes.string,
  disableButton: PropTypes.bool.isRequired,
  isDetailCommunication: PropTypes.bool,
  isClientsSelectorDisabled: PropTypes.bool,
};

export default ContactNotificationForm;
